import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import NavBar from '../layout/NavBar';
import ReactTable from 'react-table';
import { toast } from 'react-toastify';
import { getCallbackPerformanceStats } from '../../services/api/offline-consult';

const centeredCell = {
  textAlign: 'center',
  fontWeight: '600',
  alignContent: 'center',
};

const makeColoredCell = (positiveColor) => ({ value }) => (
  <span style={{ color: value > 0 ? positiveColor : 'inherit' }}>{value}</span>
);

const GreenCell = makeColoredCell('#16A34A');
const RedCell = makeColoredCell('#EF4444');

const getDefaultDates = () => ({
  from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
  to: moment().format('YYYY-MM-DD'),
});

const CallbackPerformance = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const defaults = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getCallbackPerformanceStats(fromDate, toDate);
      setDataSource(res.data || []);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error(err);
      toast.error('Failed to load callback performance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: 'Agent',
        columns: [
          {
            Header: 'Agent Name',
            accessor: 'agentName',
            width: 180,
            filterable: true,
            style: { textAlign: 'center', fontWeight: '600', alignContent: 'center' },
          },
        ],
      },
      {
        Header: 'Callback Summary',
        columns: [
          { Header: 'Total Callbacks', accessor: 'totalCallbacks', width: 140, style: centeredCell },
          {
            Header: 'Completed',
            accessor: 'callbacksCompleted',
            width: 120,
            style: centeredCell,
            Cell: GreenCell,
          },
          {
            Header: 'Not Answered',
            accessor: 'callbacksNotAnswered',
            width: 120,
            style: centeredCell,
            Cell: RedCell,
          },
          { Header: 'Scheduled', accessor: 'callbacksScheduled', width: 120, style: centeredCell },

          { Header: 'Scheduled Ids', accessor: 'scheduledCrIds', width: 180, style: centeredCell },
          {
            Header: 'Time Exceeded',
            accessor: 'timeExceededCallbacks',
            width: 130,
            style: centeredCell,
            Cell: RedCell,
          },
        ],
      },
    ],
    []
  );

  const tableStyle = {
    border: 'none',
    fontSize: '15px',
    backgroundColor: '#FFFFFF',
    fontWeight: '500',
    fontFamily: 'Montserrat',
    padding: '10px',
  };

  return (
    <div style={{ backgroundColor: '#F2F1F6', width: '100%' }}>
      <div>
        <NavBar />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          marginTop: '72px',
          marginLeft: '20px',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
            }}
          >
            <div style={{ marginLeft: '60px' }}>
              <h1>Callback Performance</h1>
              {lastRefreshed && (
                <p style={{ fontSize: '12px', color: '#888', marginTop: '-10px' }}>
                  Last refreshed: {lastRefreshed.toLocaleTimeString()}
                </p>
              )}
              <p style={{ fontSize: '12px', color: '#888', marginTop: '-6px' }}>
                Note: Default data is for the last 7 days.
              </p>
            </div>
            <div
              style={{
                marginRight: '60px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
                  From:
                </label>
                <input
                  type="date"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{
                    padding: '8px',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    fontSize: '13px',
                    fontFamily: 'Inter, sans-serif',
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
                  To:
                </label>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{
                    padding: '8px',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    fontSize: '13px',
                    fontFamily: 'Inter, sans-serif',
                  }}
                />
              </div>
              <button
                style={{
                  background: '#714FFF',
                  borderColor: '#714FFF',
                  margin: '0px',
                  borderRadius: '5px',
                  border: '0px',
                  color: 'white',
                  padding: '12px 12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Inter, sans-serif',
                }}
                onClick={fetchData}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Apply'}
              </button>
            </div>
          </div>
          <div style={{ padding: '50px' }}>
            <ReactTable
              style={tableStyle}
              showPaginationTop
              data={dataSource}
              columns={columns}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallbackPerformance;
